import React, { useEffect, useState } from "react";
import {
  CreatableSelect,
  CreatableSelectOption,
} from "@components/creatableSelect/CreatableSelect";
import {
  CreateLabIndicatorDefinitionInput,
  CreateLabResultInput,
  LabIndicatorDefinition,
  LabIndicatorValueType,
  Laboratory,
} from "generated/graphql";
import { useInjection } from "@di/DependencyContext";
import { LabIndicatorDefinitionService } from "@modules/lab-indicator-definition/lab-indicator-definition.service";
import { DependencyType } from "@di/DependencyType";
import { LoadingState } from "@util/loadingstate.util";
import { parseServerError } from "@util/parseError";
import { toast } from "sonner";
import { LabResultField } from "@pages/soil-samples/lab-result/LabResultField";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Check, Loader2 } from "lucide-react";

export interface CreateDefinitionProps {
  onChangeCreateInput: (input: CreateLabResultInput) => void;
  laboratories: Laboratory[];
  onCreateLaboratory: (lab: Laboratory) => void;
  disabled?: boolean;
  indicator?: Partial<LabIndicatorDefinition>;
}

export const CreateDefinition: React.FC<CreateDefinitionProps> = (props) => {
  const [newName, setNewName] = useState<string>();
  const [createInput, setCreateInput] =
    useState<CreateLabIndicatorDefinitionInput>({
      name: "",
      unit: "",
      valueType: LabIndicatorValueType.Continuous,
      alwaysShow: false,
    });
  const [labIndicatorDefinitions, setLabIndicatorDefinitions] =
    useState<Partial<LabIndicatorDefinition>[]>(); // todo: should we pass this in?
  const labIndicatorDefinitionService =
    useInjection<LabIndicatorDefinitionService>(
      DependencyType.LabIndicatorDefinitionService
    );
  const [selectedOption, setSelectedOption] = useState<CreatableSelectOption>();
  const [acceptedIndicator, setAcceptedIndicator] = useState<
    Partial<LabIndicatorDefinition> | undefined
  >(props.indicator ?? undefined);
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Idle
  );
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  useEffect(() => {
    void labIndicatorDefinitionService
      .findAll()
      .then((defs) => {
        setLabIndicatorDefinitions((defs?.nodes ?? []) as Partial<LabIndicatorDefinition>[]);
        setLoadingState(LoadingState.Loaded);
      })
      .catch(() => {
        setLoadingState(LoadingState.Error);
      });
  }, []);

  const handleAccept = () => {
    if (newName && newName === selectedOption?.value) {
      setCreateInput({
        ...createInput,
        name: newName,
      });
      setModalVisible(true);
    } else {
      console.log("Accepting an existing one");
      setNewName(undefined);
      setAcceptedIndicator(
        labIndicatorDefinitions?.find((def) => def.id === selectedOption?.value)
      );
    }
  };

  const handleCreateDefinition = async () => {
    try {
      setLoadingState(LoadingState.Loading);
      const created = await labIndicatorDefinitionService.create(createInput);
      if (created) {
        setAcceptedIndicator(created);
        setModalVisible(false);
      }
      setLoadingState(LoadingState.Loaded);
    } catch (e) {
      const parsed = parseServerError(e);
      setLoadingState(LoadingState.Error);
      console.error("Error creating indicator", e);
      toast.error(parsed.message);
    }
  };

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        {!acceptedIndicator && (
          <p className="text-sm font-semibold text-muted-foreground">
            New result
          </p>
        )}
        <div className="flex w-full items-center gap-2">
          {!acceptedIndicator ? (
            <>
              <CreatableSelect
                className="flex-1"
                options={[
                  ...(labIndicatorDefinitions
                    ? labIndicatorDefinitions.map((def) => ({
                        value: def.id!,
                        label: def.name!,
                      }))
                    : []),
                  ...(newName ? [{ value: newName, label: newName }] : []),
                ]}
                onCreateOption={(val) => {
                  setNewName(val);
                  setSelectedOption({ value: val, label: val });
                  return Promise.resolve({ value: val, label: val });
                }}
                onChange={(option) => {
                  setSelectedOption(option);
                }}
                placeholder="Select an indicator"
                disabled={props.disabled}
                loading={
                  props.disabled || loadingState === LoadingState.Loading
                }
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={handleAccept}
                disabled={
                  props.disabled ||
                  loadingState === LoadingState.Loading ||
                  !selectedOption
                }
              >
                {loadingState === LoadingState.Loading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
                <span className="sr-only">Accept indicator</span>
              </Button>
            </>
          ) : (
            <LabResultField
              indicator={acceptedIndicator}
              onChangeCreateInput={props.onChangeCreateInput}
              laboratories={props.laboratories}
              onCreateLaboratory={props.onCreateLaboratory}
              disabled={props.disabled}
            />
          )}
        </div>
      </div>
      <Dialog
        open={modalVisible}
        onOpenChange={(open) => {
          if (!open) {
            setModalVisible(false);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Create new indicator type</DialogTitle>
            <DialogDescription>
              Please enter the name, unit, and type of value to create a new
              indicator.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="indicator-name">Indicator name</Label>
              <Input
                id="indicator-name"
                placeholder="e.g. Microbial biomass"
                value={createInput.name}
                onChange={(event) =>
                  setCreateInput({
                    ...createInput,
                    name: event.target.value,
                  })
                }
                required
                disabled={
                  loadingState === LoadingState.Loading || props.disabled
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="indicator-unit">Unit</Label>
              <Input
                id="indicator-unit"
                placeholder="e.g. mg/mL"
                value={createInput.unit ?? ""}
                onChange={(event) =>
                  setCreateInput({
                    ...createInput,
                    unit: event.target.value,
                  })
                }
                disabled={
                  loadingState === LoadingState.Loading || props.disabled
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="indicator-type">Type</Label>
              <Select
                value={createInput.valueType}
                onValueChange={(value) =>
                  setCreateInput({
                    ...createInput,
                    valueType: value as LabIndicatorValueType,
                  })
                }
                disabled={
                  loadingState === LoadingState.Loading || props.disabled
                }
              >
                <SelectTrigger id="indicator-type">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LabIndicatorValueType.Continuous}>
                    Continuous
                  </SelectItem>
                  <SelectItem value={LabIndicatorValueType.Categorical}>
                    Categorical
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="indicator-always-show"
                checked={createInput.alwaysShow}
                onCheckedChange={(checked) =>
                  setCreateInput({
                    ...createInput,
                    alwaysShow: checked === true,
                  })
                }
                disabled={
                  loadingState === LoadingState.Loading || props.disabled
                }
              />
              <Label htmlFor="indicator-always-show" className="text-sm">
                Always show on lab results entry form?
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModalVisible(false)}
              disabled={loadingState === LoadingState.Loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDefinition}
              disabled={loadingState === LoadingState.Loading}
            >
              {loadingState === LoadingState.Loading && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Create indicator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
